import { NavLink, Outlet } from 'react-router-dom'
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import * as React from 'react'
import { Col, Container, Row, ToastContainer } from 'react-bootstrap'
import { MenuItem, Sidebar, Menu, menuClasses, type MenuItemStyles, sidebarClasses } from 'react-pro-sidebar'
import { type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { SidebarHeader } from './SidebarHeader'
import { SidebarFooter } from './SidebarFooter'
import { LayoutHeader } from './LayoutHeader'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useOutsideClick } from '../../common/useOutsideClick'
import { Toast } from '../../components/Toast/Toast'
import { removeToast, selectToasts } from '../../store/toast/toastSlice'
import { selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useGetUserSchedulingEventTypesQuery } from '../../store/scheduling/scheduling'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { HeaderContextProvider } from './HeaderContextProvider'
import styled from '@emotion/styled'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { useCustomWorkflowsEnabled } from '../../common/useCustomWorkflowsEnabled'
import './LayoutWithLeftSidebar.scss'
import { LiveIcon } from '../../components/LiveMeetings/LiveIcon'
import { SchedulingMenuIcon } from '../../components/Scheduling/SchedulingMenuIcon'
import { Divider } from '../../components/Divider/Divider'
import { useTrackedMenuItem } from './useTrackedMenuItem'
import { wheelDeltaInPixels } from './wheelDelta'
import { isInsightsEnabled } from '../../common/constants'
import { useIframeMessageHandler } from '../../common/useIframeMessageHandler'
import { FeatureAdvertisement } from '../../components/FeatureAdvertisement/FeatureAdvertisement'
import { LayoutHeaderDummy } from './LayoutHeader'
import { GhostingBanner } from '../../components/GhostingBanner/GhostingBanner'
import { OnboardingProgressWidget } from '../Growth/Onboarding/OnboardingProgressWidget/OnboardingProgressWidget'
import { OnboardingGate } from '../Growth/Onboarding/OnboardingGate'
import { OnboardingCompletedModal } from '../Growth/Onboarding/OnboardingModals/OnboardingCompletedModal/OnboardingCompletedModal'

interface LayoutProps extends PropsWithChildren {
  containerClassName?: string
}

const menuItemStyles: MenuItemStyles = {
  root: {
    color: 'white',
    borderRadius: '0.25rem',
  },
  icon: {
    color: 'white',
    width: 'auto',
    minWidth: 'auto',
    [`&.${menuClasses.disabled}`]: {
      color: 'white',
    },
    marginRight: 0,
  },
  SubMenuExpandIcon: {
    color: '#b6b7b9',
  },
  subMenuContent: ({ level }) => ({
    backgroundColor: level === 0 ? 'blue' : 'transparent',
  }),
  button: {
    height: '2.125rem',
    padding: '0.5rem',
    gap: '0.75rem',

    [`&.${menuClasses.disabled}`]: {
      color: 'grey',
    },
    '&:hover': {
      borderRadius: '0.25rem',
      background: 'var(--zocks-gray-800)',
      color: 'white',
    },
    [`&.active`]: {
      borderRadius: '0.25rem',
      color: 'white',
      backgroundColor: 'var(--zocks-primary)',
      i: { color: 'white' },
    },
  },
  label: ({ open }) => ({
    fontWeight: open ? 600 : undefined,
  }),
}

// Sub-pixel scroll offsets and rounding keep `scrollTop + clientHeight` just short of `scrollHeight`
const AT_BOTTOM_TOLERANCE_PX = 8

const ZocksMenuContainer = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  scrollbar-width: none;
  min-height: 0;
`

const NavScrollAffordance: React.FC<{ navRef: React.RefObject<HTMLDivElement>; collapsed: boolean }> = ({ navRef, collapsed }) => {
  const [hasOverflow, setHasOverflow] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const detachWheelRef = useRef<() => void>()

  const checkScroll = useCallback(() => {
    const el = navRef.current
    if (!el) {
      return
    }
    setHasOverflow(el.scrollHeight > el.clientHeight)
    setIsAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - AT_BOTTOM_TOLERANCE_PX)
  }, [navRef])

  useEffect(() => {
    const el = navRef.current
    if (!el) {
      return
    }

    let pendingRaf = -1
    const deferredCheck = () => {
      cancelAnimationFrame(pendingRaf)
      pendingRaf = requestAnimationFrame(checkScroll)
    }

    deferredCheck()

    el.addEventListener('scroll', deferredCheck)

    const ro = new ResizeObserver(deferredCheck)
    ro.observe(el)

    const mo = new MutationObserver(deferredCheck)
    mo.observe(el, { childList: true, subtree: true })

    return () => {
      cancelAnimationFrame(pendingRaf)
      el.removeEventListener('scroll', deferredCheck)
      ro.disconnect()
      mo.disconnect()
    }
  }, [checkScroll, navRef])

  // The badge row overlays the list, so wheeling over it has to scroll the list rather than nothing.
  // A callback ref binds the listener to the node itself, so mounting the row is all that drives it.
  const badgeRowRef = useCallback(
    (badgeRow: HTMLDivElement | null) => {
      detachWheelRef.current?.()
      detachWheelRef.current = undefined
      if (!badgeRow) {
        return
      }
      const onWheel = (e: WheelEvent) => {
        const el = navRef.current
        if (!el) {
          return
        }
        e.preventDefault()
        el.scrollTop += wheelDeltaInPixels(e, el)
      }
      badgeRow.addEventListener('wheel', onWheel, { passive: false })
      detachWheelRef.current = () => badgeRow.removeEventListener('wheel', onWheel)
    },
    [navRef],
  )

  useEffect(() => () => detachWheelRef.current?.(), [])

  const handleClick = () => {
    const el = navRef.current
    if (!el) {
      return
    }
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }

  if (collapsed || !hasOverflow) {
    return null
  }

  return (
    <div className={classNames('nav-scroll-affordance', { 'nav-scroll-affordance--hidden': isAtBottom })}>
      <div ref={badgeRowRef} className='nav-scroll-affordance__badge-row d-flex justify-content-center pb-2'>
        <button
          type='button'
          onClick={handleClick}
          className='nav-scroll-affordance__badge d-inline-flex align-items-center button-small text-white text-nowrap zocks-border-gray-600 zocks-border-1.0 zocks-py-0.25 zocks-px-0.75'
          tabIndex={isAtBottom ? -1 : 0}
        >
          Scroll for more <i className='bi bi-chevron-down ms-1' />
        </button>
      </div>
    </div>
  )
}

export const LayoutToastContainer: React.FC = () => {
  const dispatch = useAppDispatch()
  const toasts = useAppSelector(selectToasts)

  return (
    <ToastContainer position='bottom-center' className='p-3 position-fixed'>
      {toasts.map((toast) => (
        <Toast key={toast.id} onClose={() => dispatch(removeToast(toast.id))} variant={toast.variant} title={toast.title} delay={toast.delay}>
          {toast.messages?.length === 1 ? (
            <div>{toast.messages[0]}</div>
          ) : toast.messages?.length ? (
            <ul>
              {toast.messages.map((message, i) => (
                <li key={`${toast.id}-message-${i}`}>{message}</li>
              ))}
            </ul>
          ) : null}
        </Toast>
      ))}
    </ToastContainer>
  )
}

export const LayoutWithLeftSidebar: React.FC<LayoutProps> = ({ containerClassName, children }) => {
  useIframeMessageHandler()
  const [collapsed, setCollapsed] = useState<boolean>(false)
  const [isSidebarFixed, setSidebarFixed] = useState<boolean>(true)
  const navListRef = useRef<HTMLDivElement>(null)
  const pointerOverSidebarRef = useRef(false)
  const footerPopoverOpenRef = useRef(false)

  const onSidebarMouseEnter = () => {
    pointerOverSidebarRef.current = true
    if (!isSidebarFixed) {
      setCollapsed(false)
    }
  }
  const onSidebarMouseLeave = () => {
    pointerOverSidebarRef.current = false
    // The footer popover renders outside the sidebar bounds, so reaching it fires this handler — stay expanded until it closes
    if (!isSidebarFixed && !footerPopoverOpenRef.current) {
      setCollapsed(true)
    }
  }

  const onFooterPopoverOpenChange = useCallback(
    (open: boolean) => {
      footerPopoverOpenRef.current = open
      if (!open && !isSidebarFixed && !pointerOverSidebarRef.current) {
        setCollapsed(true)
      }
    },
    [isSidebarFixed],
  )

  const toggleSidebarFixed = () => {
    const newFixed = !isSidebarFixed
    setSidebarFixed(newFixed)
    setCollapsed(!newFixed)
  }

  return (
    <Container fluid className='d-flex flex-column p-0 h-100'>
      <LayoutToastContainer />
      <Row className='content-row'>
        <Col className='p-0 h-100'>
          <Container fluid className='p-0 h-100'>
            <div className='h-100 d-flex'>
              <div className={classNames('d-lg-block d-none zocks-sidebar-container')}>
                <Sidebar
                  className='zocks-sidebar h-100'
                  collapsed={collapsed}
                  toggled={isSidebarFixed}
                  onBackdropClick={() => setSidebarFixed(false)}
                  customBreakPoint='992'
                  onMouseEnter={onSidebarMouseEnter}
                  onMouseLeave={onSidebarMouseLeave}
                  collapsedWidth='72px'
                  width='232px'
                  rootStyles={{
                    [`.${sidebarClasses.container}`]: {
                      backgroundColor: 'var(--zocks-gray-900)',
                      overflow: 'visible',
                    },
                    borderColor: 'var(--zocks-gray-900)',
                    backgroundColor: 'var(--zocks-gray-900)',
                  }}
                >
                  <div className='d-flex flex-column h-100'>
                    <SidebarHeader isSidebarFixed={isSidebarFixed} toggleSidebar={toggleSidebarFixed} collapsed={collapsed} />
                    <ZocksMenuContainer ref={navListRef}>
                      <ZocksMenu collapsed={collapsed} />
                    </ZocksMenuContainer>
                    <NavScrollAffordance navRef={navListRef} collapsed={collapsed} />
                    <SidebarFooter collapsed={collapsed} menuItemStyles={menuItemStyles} onPopoverOpenChange={onFooterPopoverOpenChange} />
                  </div>
                </Sidebar>
              </div>
              <FloatingMenu />
              <div
                className={classNames('d-flex flex-column w-100', {
                  'content--not-fixed': !isSidebarFixed,
                  'content--fixed': isSidebarFixed,
                })}
              >
                <GhostingBanner />
                <HeaderContextProvider>
                  <LayoutHeader />
                  <Container className={classNames('mt-6 content-size zocks-pb-10.0 px-0', containerClassName)}>{children ?? <Outlet />}</Container>
                </HeaderContextProvider>
              </div>
            </div>
          </Container>
        </Col>
      </Row>
    </Container>
  )
}

const FloatingMenu = () => {
  const menuRef = useRef(null)
  const [isMenuOpen, setMenuOpen] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useOutsideClick(containerRef, () => {
    setMenuOpen(false)
  })

  useEffect(() => {
    if (!menuRef.current) {
      return
    }
    if (isMenuOpen) {
      disableBodyScroll(menuRef.current)
    } else {
      enableBodyScroll(menuRef.current)
    }

    return () => {
      clearAllBodyScrollLocks()
    }
  }, [isMenuOpen])

  const onMenuItemClick = () => {
    setMenuOpen(false)
  }

  return (
    <Container ref={containerRef} className='d-flex flex-column d-lg-none floating-menu-wrapper '>
      <Row>
        <Col xs={12} sm={8} md={6}>
          <Sidebar
            ref={menuRef}
            className={classNames('floating-sidebar zocks-sidebar', { 'd-none': !isMenuOpen })}
            width='100%'
            rootStyles={{
              [`.${sidebarClasses.container}`]: {
                backgroundColor: 'var(--zocks-gray-900)',
                borderRadius: '0 1rem 1rem 0',
              },
              borderColor: 'var(--zocks-gray-900)',
              backgroundColor: 'var(--zocks-gray-900)',
              borderRadius: '0 1rem 1rem 0',
            }}
          >
            <div className='d-flex flex-column'>
              <ZocksMenuContainer className='pt-4 pb-3 mb-0'>
                <ZocksMenu collapsed={false} onItemClick={onMenuItemClick} />
              </ZocksMenuContainer>
              <SidebarFooter collapsed={false} menuItemStyles={menuItemStyles} onItemClick={onMenuItemClick} isMobile />
            </div>
          </Sidebar>
          <div
            role='button'
            onClick={() => {
              setMenuOpen(!isMenuOpen)
            }}
            className='hamburger-fab'
          >
            {isMenuOpen ? <i className='bi bi-x' /> : <i className='bi bi-list' />}
          </div>
        </Col>
      </Row>
    </Container>
  )
}

interface ZocksMenuProps {
  collapsed: boolean
  onItemClick?: () => void
  liveIcon?: React.ReactNode
}

const ZocksMenu: React.FC<ZocksMenuProps> = ({ collapsed, onItemClick, liveIcon = <LiveIcon /> }) => {
  const { hasQuota } = useZocksQuota()
  const { enabled: customWorkflowsEnabled } = useCustomWorkflowsEnabled()
  const insightsEnabled = isInsightsEnabled()
  const emailAvailable = hasQuota('integration.emailEnabled.value')
  const schedulingEnabled = hasQuota('scheduling.enabled.value')
  const documentAvailable = hasQuota('ai.documentSummaryAgent.value')

  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { data: userEventTypeIds } = useGetUserSchedulingEventTypesQuery(
    { accountId: authenticatedUser.accountId, userId: authenticatedUser.id },
    { skip: !schedulingEnabled },
  )
  const showSchedulingNewBadge = userEventTypeIds == null ? false : userEventTypeIds.length === 0

  const handleMenuItemClick = useTrackedMenuItem(onItemClick)

  return (
    <>
      <OnboardingGate>
        <OnboardingProgressWidget collapsed={collapsed} />
      </OnboardingGate>
      <OnboardingCompletedModal />
      <div className='d-flex flex-column zocks-gap-2.5 p-3'>
        <div className='d-flex flex-column gap-2'>
          <Menu menuItemStyles={menuItemStyles} className='zocks-menu'>
            <MenuItem onClick={() => handleMenuItemClick('liveMeetings')} icon={liveIcon} component={<NavLink to='/workspace/live-meetings' />}>
              <div className='button-small'>Live Meetings</div>
            </MenuItem>
            <MenuItem
              onClick={() => handleMenuItemClick('upcoming')}
              icon={<i className='bi bi-calendar4-week' />}
              component={<NavLink to='/workspace/upcoming' />}
            >
              <div className='button-small'>Upcoming</div>
            </MenuItem>
            <MenuItem
              onClick={() => handleMenuItemClick('pastMeetings')}
              icon={<i className='bi bi-collection' />}
              component={<NavLink to='/workspace/past-meetings' />}
            >
              <div className='button-small'>Past Meetings</div>
            </MenuItem>

            <ScopedBlock
              scope={{
                scope: 'room_session:start',
                superScope: ['room_session:admin', 'room:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <MenuItem
                onClick={() => handleMenuItemClick('inPersonMeeting')}
                icon={<i className='bi bi-record-circle' />}
                component={<NavLink to='/workspace/in-person-meeting/mobile-support' />}
              >
                <div className='button-small'>In Person Meeting</div>
              </MenuItem>
            </ScopedBlock>
          </Menu>
        </div>
      </div>
      <Divider className='zocks-bg-color-gray-800 divider-sidebar-menu' />
      <div className='d-flex flex-column zocks-gap-2.5 p-3'>
        <div className='d-flex flex-column gap-2'>
          <Menu menuItemStyles={menuItemStyles} className='zocks-menu'>
            <FeatureAdvertisement bypass={schedulingEnabled}>
              <MenuItem onClick={() => handleMenuItemClick('scheduling')} icon={<SchedulingMenuIcon />} component={<NavLink to='/scheduling' />}>
                <div className='d-flex align-items-center justify-content-between w-100'>
                  <div className='button-small'>Scheduling</div>
                  {showSchedulingNewBadge ? <div className='button-small zocks-py-0.125 px-1 zocks-border-0.25 bg-white zocks-text-primary'>New</div> : null}
                </div>
              </MenuItem>
            </FeatureAdvertisement>
            <FeatureAdvertisement bypass={emailAvailable}>
              <ScopedBlock
                scope={{
                  scope: emailAvailable ? 'email_type:list' : 'room:join', // room:join link should be clickable for all users when the quota is disabled because we want to suggest them to upgrade their subscription.
                  superScope: ['email_type:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <MenuItem
                  onClick={() => handleMenuItemClick('inbox')}
                  icon={
                    <div className='d-inline-flex position-relative'>
                      <i className='bi bi-envelope' />
                    </div>
                  }
                  component={<NavLink to='/email/inbox' />}
                >
                  <div className='button-small'>Inbox</div>
                </MenuItem>
              </ScopedBlock>
            </FeatureAdvertisement>
            <FeatureAdvertisement bypass={documentAvailable}>
              <ScopedBlock
                scope={{
                  scope: documentAvailable ? 'document:list' : 'room:join',
                  superScope: ['document:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <MenuItem
                  onClick={() => handleMenuItemClick('documents')}
                  icon={
                    <div className='d-inline-flex position-relative'>
                      <i className='bi bi-file-earmark-richtext' />
                    </div>
                  }
                  component={<NavLink to='/documents/upload' />}
                >
                  <div className='button-small'>Documents</div>
                </MenuItem>
              </ScopedBlock>
            </FeatureAdvertisement>
            <MenuItem
              onClick={() => handleMenuItemClick('contacts')}
              icon={<i className='bi bi-person-lines-fill' />}
              component={<NavLink to='/library/manage-contacts' />}
            >
              <div className='button-small'>Contacts</div>
            </MenuItem>
            <ScopedBlock
              scope={{
                scope: 'insights:read',
                superScope: [],
              }}
              missingScopeAction={MissingScopeAction.HIDE}
            >
              <FeatureAdvertisement bypass={hasQuota('insights.enabled.value')}>
                {insightsEnabled ? (
                  <MenuItem onClick={() => handleMenuItemClick('insights')} icon={<i className='bi bi-lightbulb' />} component={<NavLink to='/insights' />}>
                    <div className='button-small'>Insights</div>
                  </MenuItem>
                ) : null}
                <MenuItem
                  onClick={() => handleMenuItemClick('dashboard')}
                  icon={<i className='bi bi-graph-up' />}
                  component={<NavLink to='/analytics-hub' end />}
                >
                  <div className='button-small'>Analytics</div>
                </MenuItem>
              </FeatureAdvertisement>
            </ScopedBlock>
            <FeatureAdvertisement bypass={hasQuota('ai.crossClientQuery.value')}>
              <MenuItem onClick={() => handleMenuItemClick('cross-client')} icon={<i className='bi bi-x-diamond' />} component={<NavLink to='/cross-client' />}>
                <div className='button-small'>Client Queries</div>
              </MenuItem>
            </FeatureAdvertisement>
            {customWorkflowsEnabled && (
              <MenuItem
                onClick={() => handleMenuItemClick('customWorkflowDashboard')}
                icon={<i className='bi bi-speedometer2' />}
                component={<NavLink to='/workspace/dashboard' />}
              >
                <div className='button-small'>Agents</div>
              </MenuItem>
            )}
          </Menu>
        </div>
      </div>
    </>
  )
}

export const LayoutWithLeftSidebarDummy: React.FC = () => {
  return (
    <Container fluid className='d-flex flex-column p-0 h-100'>
      <LayoutToastContainer />
      <Row className='content-row'>
        <Col className='p-0 h-100'>
          <Container fluid className='p-0 h-100'>
            <div className='h-100 d-flex'>
              <div className={classNames('d-lg-block d-none zocks-sidebar-container')}>
                <Sidebar
                  className='zocks-sidebar h-100'
                  collapsed={false}
                  toggled={false}
                  customBreakPoint='992'
                  collapsedWidth='72px'
                  width='232px'
                  rootStyles={{
                    [`.${sidebarClasses.container}`]: {
                      backgroundColor: 'var(--zocks-gray-900)',
                      overflow: 'visible',
                    },
                    borderColor: 'var(--zocks-gray-900)',
                    backgroundColor: 'var(--zocks-gray-900)',
                  }}
                >
                  <div className='d-flex flex-column h-100'>
                    <SidebarHeader isSidebarFixed toggleSidebar={() => {}} collapsed={false} />
                    <ZocksMenuContainer>
                      <ZocksMenu collapsed={false} liveIcon={<i className='bi bi-broadcast' />} />
                    </ZocksMenuContainer>
                    <SidebarFooter collapsed={false} menuItemStyles={menuItemStyles} />
                  </div>
                </Sidebar>
              </div>
              <div className={'d-flex flex-column w-100 content--fixed'}>
                <GhostingBanner />
                <HeaderContextProvider>
                  <LayoutHeaderDummy />
                  <Container className={'mt-6 content-size zocks-pb-10.0 px-0'}></Container>
                </HeaderContextProvider>
              </div>
            </div>
          </Container>
        </Col>
      </Row>
    </Container>
  )
}
